import { createServerFn } from "@tanstack/react-start";
import { and, eq } from "drizzle-orm";
import { getDb } from "@/db";
import { cars, events, heatAssignments, heats } from "@/db/schema";
import { getSession } from "@/lib/auth-session";
import { generateAvatar } from "@/lib/avatar";

export const getRegistrationData = createServerFn({ method: "GET" })
	.inputValidator((eventId: string) => eventId)
	.handler(async ({ data: eventId }) => {
		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, Number(eventId)))
			.get();
		if (!event) throw new Error("Event not found");

		const session = await getSession();
		const userId = session?.user?.id ?? null;

		// Check if user already registered
		let existingCar = null;
		if (userId) {
			existingCar = await db
				.select()
				.from(cars)
				.where(and(eq(cars.eventId, event.id), eq(cars.userId, userId)))
				.get();
		}

		// Get next available car number
		const eventCars = await db
			.select()
			.from(cars)
			.where(eq(cars.eventId, event.id));
		const usedNumbers = new Set(eventCars.map((c) => c.carNumber));
		let nextNumber = 1;
		while (usedNumbers.has(nextNumber)) nextNumber++;

		const divisions: string[] = (() => {
			try {
				return JSON.parse(event.divisions);
			} catch {
				return [];
			}
		})();

		return {
			event: {
				id: event.id,
				name: event.name,
				date: event.date,
				location: event.location,
				status: event.status,
				registrationMode: event.registrationMode,
				carNumberMode: event.carNumberMode,
				divisions,
			},
			userId,
			existingCar,
			nextNumber,
		};
	});

export const registerCar = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			eventId: number;
			driverName: string;
			carName: string;
			carNumber: number | null;
			division: string;
		}) => data,
	)
	.handler(async ({ data }) => {
		const session = await getSession();
		if (!session?.user?.id) throw new Error("Must be logged in to register");

		const db = getDb();
		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, data.eventId))
			.get();
		if (!event) throw new Error("Event not found");

		if (event.status !== "setup" && event.status !== "qualifying") {
			throw new Error("Registration is closed for this event");
		}

		if (event.registrationMode !== "open") {
			throw new Error("Self-registration is not available for this event");
		}

		// Check for duplicate registration
		const existing = await db
			.select()
			.from(cars)
			.where(
				and(eq(cars.eventId, data.eventId), eq(cars.userId, session.user.id)),
			)
			.get();
		if (existing) throw new Error("You already have a car registered");

		// Determine car number with retry for race conditions
		// In 'auto' mode, always auto-assign regardless of what user submitted
		let carNumber = event.carNumberMode === "auto" ? null : data.carNumber;
		let car: typeof cars.$inferSelect;
		const MAX_RETRIES = 3;
		for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
			if (!carNumber || attempt > 0) {
				const eventCars = await db
					.select()
					.from(cars)
					.where(eq(cars.eventId, data.eventId));
				const usedNumbers = new Set(eventCars.map((c) => c.carNumber));
				carNumber = data.carNumber ?? 1;
				while (usedNumbers.has(carNumber)) carNumber++;
			}

			try {
				const [inserted] = await db
					.insert(cars)
					.values({
						eventId: data.eventId,
						userId: session.user.id,
						driverName: data.driverName,
						carName: data.carName || "",
						carNumber,
						division: data.division || null,
					})
					.returning();
				car = inserted;
				break;
			} catch (err) {
				// If the car number was user-specified and conflicts, don't retry
				if (data.carNumber) {
					throw new Error(`Car number #${data.carNumber} is already taken`);
				}
				// On last attempt, rethrow
				if (attempt === MAX_RETRIES - 1) throw err;
				// Otherwise retry with fresh number
				carNumber = null;
			}
		}
		// biome-ignore lint/style/noNonNullAssertion: loop always assigns or throws
		car = car!;

		// Generate avatar in background if car has a name
		if (car.carName) {
			generateAvatar(car.carName, car.id)
				.then(async (avatarUrl) => {
					if (avatarUrl) {
						await getDb()
							.update(cars)
							.set({ avatarUrl })
							.where(eq(cars.id, car.id));
					}
				})
				.catch(() => {});
		}

		return car;
	});

export const updateRegistration = createServerFn({ method: "POST" })
	.inputValidator(
		(data: {
			carId: number;
			driverName: string;
			carName: string;
			division: string;
		}) => data,
	)
	.handler(async ({ data }) => {
		const session = await getSession();
		if (!session?.user?.id) throw new Error("Must be logged in");

		const db = getDb();
		const car = await db
			.select()
			.from(cars)
			.where(and(eq(cars.id, data.carId), eq(cars.userId, session.user.id)))
			.get();
		if (!car) throw new Error("Car not found");

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, car.eventId))
			.get();
		if (!event) throw new Error("Event not found");
		if (event.status === "finals" || event.status === "complete") {
			throw new Error("Cannot edit registration after finals begin");
		}

		// Prevent division changes after schedule is generated
		const newDivision = data.division || null;
		if (newDivision !== car.division && event.status === "qualifying") {
			const heatCount = await db
				.select()
				.from(heats)
				.where(eq(heats.eventId, car.eventId));
			if (heatCount.length > 0) {
				throw new Error(
					"Cannot change division after the schedule has been generated. Ask an admin to help.",
				);
			}
		}

		await db
			.update(cars)
			.set({
				driverName: data.driverName,
				carName: data.carName || "",
				division: newDivision,
				updatedAt: new Date(),
			})
			.where(eq(cars.id, data.carId));
	});

export const withdrawRegistration = createServerFn({ method: "POST" })
	.inputValidator((carId: number) => carId)
	.handler(async ({ data: carId }) => {
		const session = await getSession();
		if (!session?.user?.id) throw new Error("Must be logged in");

		const db = getDb();
		const car = await db
			.select()
			.from(cars)
			.where(and(eq(cars.id, carId), eq(cars.userId, session.user.id)))
			.get();
		if (!car) throw new Error("Car not found");

		const event = await db
			.select()
			.from(events)
			.where(eq(events.id, car.eventId))
			.get();
		if (!event) throw new Error("Event not found");
		if (event.status === "finals" || event.status === "complete") {
			throw new Error("Cannot withdraw after finals begin");
		}

		// Delete heat assignments referencing this car before deleting the car
		const eventHeats = await db
			.select()
			.from(heats)
			.where(eq(heats.eventId, car.eventId));
		for (const heat of eventHeats) {
			await db
				.delete(heatAssignments)
				.where(
					and(
						eq(heatAssignments.heatId, heat.id),
						eq(heatAssignments.carId, carId),
					),
				);
		}

		await db.delete(cars).where(eq(cars.id, carId));
	});

export const getUserCars = createServerFn({ method: "GET" }).handler(
	async () => {
		const session = await getSession();
		if (!session?.user?.id) return [];

		const db = getDb();
		const userCars = await db
			.select({
				car: cars,
				eventName: events.name,
				eventDate: events.date,
				eventStatus: events.status,
				eventLocation: events.location,
			})
			.from(cars)
			.innerJoin(events, eq(cars.eventId, events.id))
			.where(eq(cars.userId, session.user.id));

		return userCars;
	},
);
